import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'

import { updateCertification, searchInstructorCandidates } from '../../actions/certificationActions'
import { showGenericError } from '../../utility/certificationBuilder'
import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'
import BuilderField from './BuilderField'
import Select from '../common/form/Select'

import classes from './InstructorsField.module.scss'

const STAFF_TYPES = ['Admin', 'Representative']
const SEARCH_DELAY = 300

const InstructorsField = ({ certification }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isAdding, setIsAdding] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [options, setOptions] = useState([])

  const instructorIds = certification.instructors.map(instructor => instructor.id)

  const search = useMemo(() => debounce((term) => {
    if (term.trim() === '') { setOptions([]); return }

    setIsSearching(true)
    searchInstructorCandidates(term, STAFF_TYPES)
      .then(users => setOptions(users.map(user => ({ value: user.id, label: `${user.firstName} ${user.lastName}` }))))
      .catch(() => setOptions([]))
      .finally(() => setIsSearching(false))
  }, SEARCH_DELAY), [])

  const saveInstructors = (ids) => {
    setIsSaving(true)
    dispatch(updateCertification({ id: certification.id, instructorIds: ids })).unwrap()
      .then(() => setIsAdding(false))
      .catch(showGenericError)
      .finally(() => setIsSaving(false))
  }

  return (
    <BuilderField label={t('certificationBuilder.details.instructors')}>
      <div className='d-flex flex-column'>
        {certification.instructors.map(instructor => (
          <div key={instructor.id} className={classNames('d-flex align-items-center', classes.row)}>
            <img src={instructor.avatar?.fileUrl || AvatarPlaceholder} alt='' className={classNames('flex-shrink-0', classes.avatar)} />
            <span className={classNames('flex-grow-1', classes.name)}>{instructor.firstName} {instructor.lastName}</span>
            <button
              type='button'
              className={classes.remove}
              title={t('common.action.remove')}
              onClick={() => saveInstructors(instructorIds.filter(id => id !== instructor.id))}
              disabled={isSaving}
            >
              <i className='fa fa-times' aria-hidden='true' />
            </button>
          </div>
        ))}

        {isAdding ? (
          <Select
            inputId='certificationInstructorSearch'
            value={null}
            options={options.filter(option => !instructorIds.includes(option.value))}
            onInputChange={search}
            onChange={(option) => saveInstructors([...instructorIds, option.value])}
            placeholder={t('certificationBuilder.details.searchInstructorsPlaceholder')}
            noOptionsMessage={() => t('certificationBuilder.details.noInstructorsFound')}
            isLoading={isSearching}
            isDisabled={isSaving}
            filterOption={null}
            menuPlacement='auto'
            autoFocus
            onBlur={() => setIsAdding(false)}
          />
        ) : (
          <button type='button' className={classNames('align-self-start', classes.addButton)} onClick={() => setIsAdding(true)} disabled={isSaving}>
            <i className='fa fa-plus' aria-hidden='true' />
            {t('certificationBuilder.details.addInstructor')}
          </button>
        )}
      </div>
    </BuilderField>
  )
}

InstructorsField.propTypes = {
  certification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    instructors: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.shape({ fileUrl: PropTypes.string }),
    })).isRequired,
  }).isRequired,
}

export default InstructorsField
